<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { PredicateAnswer } from '../types';
import SelectButton from 'primevue/selectbutton';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
    productPredicateRequirementId?: string;
    predicate?: {
        id?: string;
        predicateQuestion?: string | null | undefined;
    };
    answer?: PredicateValue;
}>();

const emit = defineEmits<{
    (e: 'update:answer', answer: PredicateAnswer): void;
    (e: 'collapsed'): void;
    (e: 'expanded'): void;
}>();

const expanded = ref<boolean>(false);
watch(expanded, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        if (newValue) {
            emit('expanded');
        } else {
            emit('collapsed');
        }
    }
});

const options = [
    PredicateValue.Yes,
    PredicateValue.No,
    PredicateValue.Indeterminate,
    PredicateValue.Undecidable,
];

const icon = computed(() =>
    expanded.value ? 'pi pi-chevron-down' : 'pi pi-chevron-right'
);

const onAnswered = (answer: PredicateValue) => {
    emit('update:answer', {
        predicate: props.predicate?.id,
        answer,
    });
};
</script>
<template>
    <div class="grid">
        <div class="col-1">
            <Button
                v-if="$slots.expand"
                :icon="icon"
                text
                rounded
                @click="expanded = !expanded"
            />
        </div>
        <div class="col">
            <div class="flex h-full align-items-center justify-content-start">
                <span v-html="predicate?.predicateQuestion" />
            </div>
        </div>
        <div class="col">
            <div
                class="flex h-full w-full align-items-center justify-content-center"
            >
                <SelectButton
                    :model-value="answer"
                    :options="options"
                    class="product-predicate-buttons"
                    @update:model-value="onAnswered"
                >
                    <template #option="slotProps">
                        <div class="flex align-items-center gap-2 flex-nowrap">
                            <i
                                v-if="slotProps.option === PredicateValue.Yes"
                                class="pi pi-check"
                            />
                            <i
                                v-if="slotProps.option === PredicateValue.No"
                                class="pi pi-times"
                            />
                            <i
                                v-if="
                                    slotProps.option ===
                                    PredicateValue.Indeterminate
                                "
                                class="pi pi-minus-circle"
                            />
                            <i
                                v-if="
                                    slotProps.option ===
                                    PredicateValue.Undecidable
                                "
                                class="pi pi-question-circle"
                            />
                            {{ t(slotProps.option) }}
                        </div>
                    </template>
                </SelectButton>
            </div>
        </div>
    </div>
    <div v-if="expanded">
        <slot name="expand" />
    </div>
</template>

<style>
.product-predicate-buttons.p-selectbutton .p-button {
    border-radius: 0;
    border: 1px solid #39b8e2;
    font-weight: 500;
    background-color: #39b8e2 !important;
    color: #fff !important;

    &:hover {
        background-color: #0c8ab4 !important;
        transition: background-color 0.5s, border-color 0.5s;
        color: #fff !important;
    }

    &:focus {
        background-color: #0c8ab4 !important;
        transition: background-color 0.5s, border-color 0.5s;
        color: #fff !important;
    }
}

.product-predicate-buttons.p-selectbutton .p-button:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    &:hover {
        border: 1px solid #0c8ab4;
    }
}

.product-predicate-buttons.p-selectbutton .p-button:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    border-left: none;

    &:hover {
        border-top: 1px solid #0c8ab4;
        border-bottom: 1px solid #0c8ab4;
        border-right: 1px solid #0c8ab4;
    }
}

.product-predicate-buttons.p-selectbutton
    .p-button:not(:first-child):not(:last-child) {
    border-left: none;
    &:hover {
        border-top: 1px solid #0c8ab4;
        border-bottom: 1px solid #0c8ab4;
        border-right: 1px solid #0c8ab4;
    }
}

.product-predicate-buttons.p-selectbutton .p-button.p-highlight {
    background-color: #0c8ab4 !important;
    border: 1px solid #0c8ab4;
    color: #fff !important;
}
</style>

<i18n>
    {
        "en": {
            "YES": "Yes",
            "NO": "No",
            "INDETERMINATE": "Indeterminate",
            "UNDECIDABLE": "Undecidable",
        },
        "nl": {
            "YES": "Ja",
            "NO": "Nee",
            "INDETERMINATE": "Onbepaald",
            "UNDECIDABLE": "Onbeslisbaar",
        },
        "fr": {
            "YES": "Oui",
            "NO": "Non",
            "INDETERMINATE": "Indéterminé",
            "UNDECIDABLE": "Indécidable",
        },
    }
</i18n>
